import { Link, Navigate } from "react-router-dom";
import { useEffect } from "react";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  Calculator,
  Timer,
  Wrench,
  ArrowRight,
  CheckCircle2,
  Sparkles,
  UtensilsCrossed,
  Users,
  MonitorSpeaker,
  Wifi,
  Grid3X3,
  Music,
  Volume2,
  ClipboardList,
  FileSearch,
  AlertTriangle,
  ArrowRightLeft,
} from "lucide-react";

const suites = [
  {
    tag: "Planning",
    accent: "text-cyan-400",
    dot: "bg-cyan-400",
    icon: Calculator,
    title: "Plan every square metre before the day.",
    blurb:
      "Room capacity, waste-zero catering, staffing shifts, AV sightlines, WiFi density, dancefloor sizing and a 2D floor plan editor — all wired together so the numbers stay honest.",
    tools: [
      { icon: Calculator, label: "Room Capacity" },
      { icon: UtensilsCrossed, label: "Waste-Zero Catering" },
      { icon: Users, label: "Staffing Planner" },
      { icon: MonitorSpeaker, label: "AV Tech Specs" },
      { icon: Wifi, label: "WiFi Predictor" },
      { icon: Grid3X3, label: "Floor Plan" },
      { icon: Music, label: "Dancefloor Calc" },
    ],
  },
  {
    tag: "Execution",
    accent: "text-yellow-400",
    dot: "bg-yellow-400",
    icon: Timer,
    title: "Run the show without radio chaos.",
    blurb:
      "A shareable master timer that syncs the whole crew, a decibel monitor that flags noise breaches at 90 dB, and a dinner service tally your servers actually enjoy using.",
    tools: [
      { icon: Timer, label: "Sync Timer" },
      { icon: Volume2, label: "Decibel Meter" },
      { icon: ClipboardList, label: "Dinner Tally" },
    ],
  },
  {
    tag: "Solver",
    accent: "text-purple-400",
    dot: "bg-purple-400",
    icon: Wrench,
    title: "AI in your pocket when things go sideways.",
    blurb:
      "Scan a supplier contract for hidden costs, generate a calm evacuation script in seconds, and convert units on the fly — because the venue in Berlin will absolutely quote you in Celsius.",
    tools: [
      { icon: FileSearch, label: "Contract Scanner" },
      { icon: AlertTriangle, label: "Emergency Script" },
      { icon: ArrowRightLeft, label: "Unit Converter" },
    ],
  },
];

const stats = [
  { value: "13", label: "purpose-built tools" },
  { value: "3", label: "event stages covered" },
  { value: "0", label: "spreadsheets required" },
];

export const Landing = () => {
  const { isAuthenticated, loading } = useAuth();
  const { theme } = useTheme();
  const logoSrc =
    theme === "light" ? "/logos/Evntkit_Logo.png" : "/logos/Evntkit_Logo_White.png";

  useEffect(() => {
    document.title = "evntkit — Planning & Delivery Tools for Event Pros";
  }, []);

  // Authenticated users skip the marketing page.
  if (!loading && isAuthenticated) {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <div className="min-h-screen bg-background bg-texture">
      {/* Top bar */}
      <header className="sticky top-0 z-40 border-b border-border/40 bg-background/80 backdrop-blur">
        <div className="container mx-auto px-4 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center" data-testid="landing-logo-link">
            <img src={logoSrc} alt="evntkit" className="h-10" />
          </Link>
          <nav className="flex items-center gap-2 sm:gap-3">
            <Link to="/help">
              <Button
                variant="ghost"
                className="text-sm hidden sm:inline-flex"
                data-testid="landing-help-link"
              >
                Help
              </Button>
            </Link>
            <Link to="/login">
              <Button
                variant="ghost"
                className="text-sm"
                data-testid="landing-login-link"
              >
                Sign in
              </Button>
            </Link>
            <Link to="/register">
              <Button
                className="text-sm bg-cyan-600 hover:bg-cyan-700 text-white"
                data-testid="landing-register-cta"
              >
                Sign up free
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </nav>
        </div>
      </header>

      {/* Hero */}
      <section className="container mx-auto px-4 pt-16 pb-20 sm:pt-24 sm:pb-28">
        <div className="grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7 space-y-6">
            <div
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-border/60 bg-secondary/40 text-xs uppercase tracking-wider text-muted-foreground"
              data-testid="landing-eyebrow"
            >
              <Sparkles className="h-3.5 w-3.5 text-cyan-400" />
              Built for event professionals, on-site, mid-crisis
            </div>
            <h1
              className="text-5xl sm:text-6xl lg:text-7xl tracking-tighter leading-[0.95]"
              data-testid="landing-hero-title"
            >
              The <span className="lowercase">evnt</span>
              <span className="lowercase font-bold">kit</span>
              <br />
              your radio doesn&rsquo;t have.
            </h1>
            <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl leading-relaxed">
              Thirteen calculators, timers, and AI helpers that quietly do the maths
              while you handle the humans. Planning, execution, and the &ldquo;oh-god-what-now&rdquo;
              moments — one browser tab, works offline, no spreadsheets harmed.
            </p>
            <div className="flex flex-wrap items-center gap-3 pt-2">
              <Link to="/register">
                <Button
                  size="lg"
                  className="bg-cyan-600 hover:bg-cyan-700 text-white h-12 px-6 text-base"
                  data-testid="landing-hero-primary-cta"
                >
                  Sign up free
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <Link to="/login">
                <Button
                  size="lg"
                  variant="outline"
                  className="h-12 px-6 text-base"
                  data-testid="landing-hero-secondary-cta"
                >
                  I already have an account
                </Button>
              </Link>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-2 pt-4 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                No credit card
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Installable PWA, works offline
              </span>
              <span className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="h-4 w-4 text-green-500" />
                Metric & imperial
              </span>
            </div>
          </div>

          {/* Hero visual — layered tool cards */}
          <div className="lg:col-span-5">
            <div className="relative aspect-[4/5] max-w-md mx-auto">
              {/* Backdrop briefcase */}
              <div
                className="absolute inset-0 rounded-2xl bg-gradient-to-br from-cyan-500/10 via-yellow-500/5 to-purple-500/10 border border-border/40 backdrop-blur-sm"
                aria-hidden
              />
              {/* Floating cards */}
              <div className="absolute top-6 left-6 right-16 p-4 rounded-lg bg-card border border-border shadow-xl">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded bg-cyan-500/15 text-cyan-400">
                    <Calculator className="h-4 w-4" />
                  </div>
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">
                    Room Capacity
                  </span>
                </div>
                <div className="flex items-baseline gap-2">
                  <span className="text-3xl font-bold">184</span>
                  <span className="text-sm text-muted-foreground">banquet, seated</span>
                </div>
              </div>

              <div className="absolute top-1/3 right-4 left-14 p-4 rounded-lg bg-card border border-border shadow-xl">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded bg-yellow-500/15 text-yellow-400">
                    <Timer className="h-4 w-4" />
                  </div>
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">
                    Sync Timer
                  </span>
                </div>
                <div className="font-mono text-3xl tabular-nums text-yellow-400">
                  00:04:12
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Code <span className="text-foreground font-mono">RUN-42B</span>
                </div>
              </div>

              <div className="absolute bottom-6 left-8 right-8 p-4 rounded-lg bg-card border border-border shadow-xl">
                <div className="flex items-center gap-2 mb-2">
                  <div className="p-2 rounded bg-purple-500/15 text-purple-400">
                    <FileSearch className="h-4 w-4" />
                  </div>
                  <span className="text-xs uppercase tracking-wider text-muted-foreground">
                    Contract Scan
                  </span>
                </div>
                <ul className="text-xs space-y-1 text-muted-foreground">
                  <li className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-red-500" />
                    <span className="text-foreground">Auto-renewal after 12mo</span>
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-amber-500" />
                    Corkage fee not itemised
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="h-1.5 w-1.5 rounded-full bg-green-500" />
                    Cancellation window fair
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats strip */}
      <section className="border-y border-border/50 bg-card/40">
        <div className="container mx-auto px-4 py-8 grid grid-cols-3 gap-4">
          {stats.map((s) => (
            <div key={s.label} className="text-center">
              <div className="text-4xl sm:text-5xl font-bold tracking-tight">
                {s.value}
              </div>
              <div className="text-xs sm:text-sm text-muted-foreground uppercase tracking-wider mt-1">
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Suites */}
      <section className="container mx-auto px-4 py-20 sm:py-28 space-y-24">
        {suites.map((suite, idx) => (
          <div
            key={suite.tag}
            className={`grid lg:grid-cols-12 gap-8 lg:gap-12 items-center ${
              idx % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""
            }`}
            data-testid={`landing-suite-${suite.tag.toLowerCase()}`}
          >
            <div className="lg:col-span-5 space-y-5">
              <div className="inline-flex items-center gap-2">
                <span className={`h-2 w-2 rounded-full ${suite.dot}`} />
                <span
                  className={`text-xs uppercase tracking-widest ${suite.accent}`}
                >
                  {suite.tag} Suite
                </span>
              </div>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight leading-tight">
                {suite.title}
              </h2>
              <p className="text-base sm:text-lg text-muted-foreground leading-relaxed">
                {suite.blurb}
              </p>
            </div>

            <div className="lg:col-span-7">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {suite.tools.map((t) => (
                  <div
                    key={t.label}
                    className="group p-4 rounded-lg border border-border/60 bg-card hover:border-border transition-colors"
                  >
                    <div
                      className={`p-2 rounded-sm bg-secondary w-fit ${suite.accent} mb-3`}
                    >
                      <t.icon className="h-4 w-4" strokeWidth={1.5} />
                    </div>
                    <div className="text-sm uppercase tracking-wide">
                      {t.label}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ))}
      </section>

      {/* Final CTA */}
      <section className="border-t border-border/50 bg-card/30">
        <div className="container mx-auto px-4 py-20 sm:py-24 text-center max-w-3xl">
          <h2 className="text-4xl sm:text-5xl tracking-tight mb-4">
            Your next event starts <span className="italic">without</span> a
            spreadsheet.
          </h2>
          <p className="text-lg text-muted-foreground mb-8">
            Free forever for solo planners. Nothing to install — but you can add
            it to your home screen in one tap.
          </p>
          <div className="flex flex-wrap items-center justify-center gap-3">
            <Link to="/register">
              <Button
                size="lg"
                className="bg-cyan-600 hover:bg-cyan-700 text-white h-12 px-8 text-base"
                data-testid="landing-footer-cta"
              >
                Create your free account
                <ArrowRight className="ml-2 h-5 w-5" />
              </Button>
            </Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-border/30">
        <div className="container mx-auto px-4 py-6 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs text-muted-foreground">
          <span>
            © {new Date().getFullYear()} evntkit • Planning & delivery tools for
            event professionals
          </span>
          <div className="flex items-center gap-4">
            <Link to="/login" className="hover:text-foreground transition-colors">
              Sign in
            </Link>
            <Link
              to="/register"
              className="hover:text-foreground transition-colors"
            >
              Sign up
            </Link>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
